import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';
import { PageSectionFieldValidator } from '../../domain/models/dynamic-form-schema.model';
import { calculateAge } from './calculate-age';
import { isValueMissing, matchesConditions } from './dynamic-form-rules';

function getControlByNames(
    control: AbstractControl,
    ...names: string[]
): AbstractControl | null {
    for (const name of names) {
        const candidate = control.get(name);
        if (candidate) {
            return candidate;
        }
    }

    return null;
}

function getValueByNames(control: AbstractControl, ...names: string[]): any {
    return getControlByNames(control, ...names)?.value;
}

export function emailRequiredValidator(control: AbstractControl): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
        const contactPreference = getValueByNames(
            control,
            'ContactPreference',
            'contactPreference',
            'contactPreferenceSelectionId',
            'ContactPreferenceSelectionId'
        );

        if (contactPreference === undefined || contactPreference === null)
            return null;

        const required = contactPreference === 18;
        return required && control.value === '' ? { required: true } : null;
    };
}

export function futureYearValidator(futureYear: number): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
        return control.value <= futureYear ? null : { futureYear: true };
    };
}

export function minimumAgeValidator(minimumAge: number): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
        let age = calculateAge(control);
        if (age === null) return null;
        const minimumAgeMet = age >= minimumAge;
        return !minimumAgeMet ? { minimumAge: true } : null;
    };
}

export function maximumAgeValidator(maximumAge: number): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
        let age = calculateAge(control);
        if (age === null) return null;
        const maximumAgeMet = age < maximumAge;
        return !maximumAgeMet ? { maximumAge: true } : null;
    };
}

export function atLeastOnePhoneFormValidator(
    control: AbstractControl
): ValidationErrors | null {
    const mobilePhone =
        getValueByNames(control, 'MobilePhone', 'mobilePhone') ?? '';
    const homePhone = getValueByNames(control, 'HomePhone', 'homePhone') ?? '';
    const workPhone = getValueByNames(control, 'WorkPhone', 'workPhone') ?? '';
    if (mobilePhone === '' && homePhone === '' && workPhone === '')
        return { atLeastOnePhoneRequired: true };
    return null;
}

export function consentTextMessageWhenCellPhoneSelectedFormValidator(
    control: AbstractControl
): ValidationErrors | null {
    const controlConsentReceiveTextMessage = getControlByNames(
        control,
        'ConsentReceiveTextMessage',
        'consentReceiveTextMessage'
    );

    const contactPreference =
        getValueByNames(
            control,
            'ContactPreference',
            'contactPreference',
            'contactPreferenceSelectionId',
            'ContactPreferenceSelectionId'
        ) ?? '';
    const consentReceiveTextMessage = controlConsentReceiveTextMessage?.value;

    if (
        controlConsentReceiveTextMessage &&
        (contactPreference === 17 || contactPreference === 19) &&
        !consentReceiveTextMessage
    )
        return { consentTextMessageRequired: true };

    return null;
}

export function computerEquipmentDisallow454CombinedValidator(
    control: AbstractControl
): ValidationErrors | null {
    const value = control.value ?? [];
    if (Array.isArray(value) && value.length > 1 && value.includes(454)) {
        return { computerEquipmentDisallow454Combined: true };
    }
    return null;
}

export function currentDateValidator(
    control: AbstractControl
): ValidationErrors | null {
    if (
        control === null ||
        control.value === undefined ||
        control.value === null ||
        control.value === ''
    )
        return null;

    const currentDate = new Date(control.value);
    const today = new Date();

    // Check if date was parsed correctly (getTime() returns NaN for invalid dates)
    if (isNaN(currentDate.getTime())) {
        return { invalidDate: true };
    }

    const currentYear = today.getFullYear();
    const dateYear = currentDate.getFullYear();

    // Reject implausible years (not current year ± 1)
    if (dateYear < currentYear - 1 || dateYear > currentYear + 1) {
        return { invalidDate: true };
    }

    // Is the date within 30 days of today?
    const thirtyDaysInMilliseconds = 30 * 24 * 60 * 60 * 1000;
    const invalidDate =
        Math.abs(currentDate.getTime() - today.getTime()) >
        thirtyDaysInMilliseconds;

    return invalidDate ? { invalidDate: true } : null;
}

export function publicAssistanceValidator(
    control: AbstractControl
): ValidationErrors | null {
    const receivingPublicAssistanceSelectionIds = getControlByNames(
        control,
        'ReceivingPublicAssistance',
        'receivingPublicAssistance',
        'receivingPublicAssistanceSelectionIds',
        'ReceivingPublicAssistanceSelectionIds'
    );

    if (
        receivingPublicAssistanceSelectionIds &&
        Array.isArray(receivingPublicAssistanceSelectionIds.value) &&
        receivingPublicAssistanceSelectionIds.value.includes(80) &&
        receivingPublicAssistanceSelectionIds.value.length > 1
    ) {
        return { publicAssistanceInvalid: true };
    }

    return null;
}

export function eligibilityCategoryValidator(
    control: AbstractControl
): ValidationErrors | null {
    const eligibilityStatusSelectionId = getControlByNames(
        control,
        'EligibilityStatus',
        'eligibilityStatus',
        'eligibilityStatusSelectionId',
        'EligibilityStatusSelectionId'
    );

    const eligibilityCategorySelectionId = getControlByNames(
        control,
        'EligibilityCategory',
        'eligibilityCategory',
        'eligibilityCategorySelectionId',
        'EligibilityCategorySelectionId'
    );
    const eligibilityCategorySelectionIdValue =
        eligibilityCategorySelectionId?.value;

    if (
        eligibilityCategorySelectionId &&
        (!eligibilityCategorySelectionIdValue ||
            (Array.isArray(eligibilityCategorySelectionIdValue) &&
                eligibilityCategorySelectionIdValue.length === 0)) &&
        (eligibilityStatusSelectionId?.value == 1015 ||
            eligibilityStatusSelectionId?.value == 1016)
    )
        return { eligibilityCategoryRequired: true };

    return null;
}

export function eligibilityExplanationValidator(
    control: AbstractControl
): ValidationErrors | null {
    const eligibilityCategorySelectionId = getControlByNames(
        control,
        'EligibilityCategory',
        'eligibilityCategory',
        'eligibilityCategorySelectionId',
        'EligibilityCategorySelectionId'
    );
    const eligibilityExplanation = getControlByNames(
        control,
        'EligibilityExplanation',
        'eligibilityExplanation'
    );
    const eligibilityExplanationValue = eligibilityExplanation?.value;

    if (
        eligibilityExplanation &&
        (!eligibilityExplanationValue ||
            eligibilityExplanationValue.trim().length === 0) &&
        (eligibilityCategorySelectionId?.value == 1023 ||
            eligibilityCategorySelectionId?.value == 1024)
    ) {
        return { eligibilityExplanationRequired: true };
    }

    return null;
}

export function requireFieldWhenFormValidator(
    config: PageSectionFieldValidator
): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
        if (!config.target || !matchesConditions(control, config.when)) {
            return null;
        }

        const targetControl = control.get(config.target);

        if (!targetControl || !isValueMissing(targetControl.value)) {
            return null;
        }

        return {
            [config.errorKey ?? config.name]: {
                target: config.target
            }
        };
    };
}
